import { useMutation, useLazyQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useTenantId } from "hooks/useTenant";
import { useCallback, useState } from "react";

export type ManifestPdfExportFilters = {
  facilityIds: string[];
  shippedDateStart: string;
  shippedDateEnd: string;
  federalWasteCodes: string[];
  stateWasteCodes: string[];
};

export type ManifestPdfExportPreview = {
  matchingManifests: number;
  manifestsWithPdf: number;
};

export type ExportStatus = "idle" | "submitting" | "error";

const MANIFEST_DOCUMENT_EXPORT_PREVIEW_QUERY = gql(`
  query ManifestDocumentExportPreview($input: ExportManifestDocumentsInput!) {
    manifestDocumentExportPreview(input: $input) {
      matchingManifests
      manifestsWithPdf
    }
  }
`);

const EXPORT_MANIFEST_DOCUMENTS_MUTATION = gql(`
  mutation ExportManifestDocuments($input: ExportManifestDocumentsInput!) {
    exportManifestDocuments(input: $input) {
      jobId
    }
  }
`);

function toExportInput(tenantId: string, filters: ManifestPdfExportFilters) {
  return {
    tenantId,
    facilityIds: filters.facilityIds,
    shippedDateStart: filters.shippedDateStart,
    shippedDateEnd: filters.shippedDateEnd,
    federalWasteCodes: filters.federalWasteCodes,
    stateWasteCodes: filters.stateWasteCodes,
  };
}

/**
 * Preview + mutation for kicking off a manifest PDF export. Polling for the
 * queued job and the progress/success/error UI live in
 * components/JobExportProgressDialog.tsx (rendered via the global
 * minimized-dialogs tray) so they keep running while this dialog closes and
 * the user navigates elsewhere. This hook only gets the job queued.
 */
export function useExportManifestPdfs() {
  // Customer-facing route is /o/{tenantId}/..., which useTenantId derives
  // from the path (falling back to the session-stored tenant). This mirrors
  // how sibling components under this route (e.g. ManifestsExportMenu) get
  // their tenantId, without threading a new prop through the dialog.
  const tenantId = useTenantId();
  const alerts = useAlerts();

  const [preview, setPreview] = useState<ManifestPdfExportPreview | undefined>(undefined);
  const [exportStatus, setExportStatus] = useState<ExportStatus>("idle");
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  // Filters change on every keystroke/selection (the dialog debounces before
  // calling loadPreview), so each call should hit the network rather than
  // resolve from a stale cache entry keyed on a previous set of variables.
  const [runPreviewQuery, { loading: previewLoading }] = useLazyQuery(
    MANIFEST_DOCUMENT_EXPORT_PREVIEW_QUERY,
    { fetchPolicy: "network-only" }
  );

  const [runExportMutation] = useMutation(EXPORT_MANIFEST_DOCUMENTS_MUTATION);

  const loadPreview = useCallback(
    (filters: ManifestPdfExportFilters) => {
      if (!tenantId) {
        return;
      }
      runPreviewQuery({ variables: { input: toExportInput(tenantId, filters) } })
        .then(({ data }) => {
          if (data?.manifestDocumentExportPreview) {
            setPreview(data.manifestDocumentExportPreview);
          }
        })
        .catch(() => {
          // Preview failures are non-fatal (the dialog just shows no
          // preview); the export mutation itself surfaces its own errors.
        });
    },
    [tenantId, runPreviewQuery]
  );

  const startExport = useCallback(
    (filters: ManifestPdfExportFilters, onJobQueued: (jobId: string) => void) => {
      if (!tenantId) {
        setExportStatus("error");
        setExportError("Missing tenant context");
        return;
      }
      setExportStatus("submitting");
      setExportError(undefined);
      runExportMutation({
        variables: { input: toExportInput(tenantId, filters) },
        onCompleted: (data) => {
          setExportStatus("idle");
          onJobQueued(data.exportManifestDocuments.jobId);
        },
        onError: (error) => {
          alerts.error(error.message);
          setExportStatus("error");
          setExportError(error.message);
        },
      });
    },
    [tenantId, runExportMutation, alerts]
  );

  const reset = useCallback(() => {
    setPreview(undefined);
    setExportStatus("idle");
    setExportError(undefined);
  }, []);

  return {
    preview,
    previewLoading,
    loadPreview,
    startExport,
    exportStatus,
    exportError,
    reset,
  };
}
